import React, { useState, useCallback, useEffect} from 'react';
import { GoogleMap, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import apiSentryDynamicsArcGIS from '../../services/api-sentry-dynamics-arcGIS';
import { FilePath, googleMapsLibraries } from '../../services/utilities';
import { fromMapOverlaysToJSON, SentryTileMapType } from '../../services/google-map-support';

const ResultsMap = ({ id, parcelsToShowOnMap, mapStateAbbrev, mapCounty, setExpandedProperty, expandedProperty, onSelectedMapProperty, reloadSelectedProperty }) => {
    const [map, setMap] = useState(null);    
	const [reloadingMap, setReloadingMap] = useState(false);    
	const [popUpOpen, setPopUpOpen] = useState(false);    
	const [popUpPosition, setPopUpPosition] = useState(null);  
	const [popUpContent, setPopUpContent] = useState(null); 
	const [geoJsonFeatures, setGeoJsonFeatures] = useState([]);
	const [listener, setListener] = useState(null);
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
		libraries: googleMapsLibraries
    });

	var selectedStyle = {
		fillColor: "#0df629",
		fillOpacity: 0.5,
		strokeColor: "#ffffff",
		strokeWeight: 3,
		strokeOpacity: 1,
		zIndex: 100
	};

	var shownStyle = {
		fillColor: "#cdcdcd",
		fillOpacity: .3,
		strokeColor: "#ed6100",
		strokeWeight: 2,
		strokeOpacity: 1,
		zIndex: 1
	}
	useEffect(() => {
		if (map && expandedProperty && id === "searchResultsMap") {		
			map.data.setStyle(function(feature) {
				map.data.revertStyle();
				return feature.getProperty('PARCELID') === expandedProperty.parcelId ? selectedStyle : shownStyle;
			}); 
			map.setCenter(new window.google.maps.LatLng(expandedProperty.latitude, expandedProperty.longitude));
			map.setZoom(18);
			setMap(map);
		}
	}, [expandedProperty, id])

	useEffect(() => {
		if (!map || !mapStateAbbrev || !mapCounty) return;
		map.overlayMapTypes.insertAt(
			0,
			new SentryTileMapType(window.google.maps, new window.google.maps.Size(256, 256), mapStateAbbrev, mapCounty)
		);

	}, [map, mapStateAbbrev, mapCounty]);

	useEffect(() => {
		if (reloadSelectedProperty) setReloadingMap(true);
	}, [reloadSelectedProperty]);

	useEffect(() => {
		if (map && parcelsToShowOnMap && reloadingMap) {
			setPopUpOpen(false);
			highlightParcelsOnMap(map, parcelsToShowOnMap);
			setReloadingMap(false);
		}
		setMap(map);
	}, [parcelsToShowOnMap, map, reloadingMap])



	const addParcelOutlineGeoJson = async (map, selectedParcels) => {
		apiSentryDynamicsArcGIS.get(mapStateAbbrev +
			"/geo_" + mapStateAbbrev.toLowerCase() + "_" + mapCounty.toLowerCase() + "/MapServer/1/query?where=PARCELID+IN+" + encodeURIComponent("(\"" + selectedParcels + "\")").replace("(", "%28").replace(")", "%29")
			+ "&f=geojson")
			.then((response) => {
				response.data.features.forEach(e => {
					if (e.geometry.type === 'Polygon') {
						e.geometry.coordinates[0].forEach(c => {
							if (c.length > 2) c.pop();
						})
					}
				});
				var features = map.data.addGeoJson(response.data);
				features.forEach(f => { geoJsonFeatures.push(f); });
				setGeoJsonFeatures(geoJsonFeatures);
			});
	};

	const highlightParcelsOnMap = (map, parcelList) => {
		const bounds = new window.google.maps.LatLngBounds();
		var selectedParcels = "";
		if (parcelList.length === 1) {
			map.setCenter(new window.google.maps.LatLng(parcelList[0].latitude, parcelList[0].longitude));
			map.setZoom(18);
			selectedParcels += "'" + parcelList[0].parcelId + "',";
		}
		else 
		{
			parcelList.forEach((result) => {
				if (result.latitude && result.longitude) {
					bounds.extend(new window.google.maps.LatLng(result.latitude, result.longitude));
				}
				selectedParcels += "'" + result.parcelId + "',";
			});
			map.fitBounds(bounds);
		}
		selectedParcels = selectedParcels.length > 5 ? selectedParcels.slice(0, -1) : selectedParcels;
		map.data.setStyle(function (feature) {
			if (feature.getProperty('PARCELID') === expandedProperty.parcelId) {
				return selectedStyle;
			}
			else {
				return shownStyle;
			}
		});

		if (setExpandedProperty) {
			if (listener) listener.remove();
			var dataListener = map.data.addListener('click', function (event) {
				setExpandedProperty(parcelList.find(r => r.parcelId === event.feature.getProperty('PARCELID')));
			});
			setListener(dataListener);
		}
		geoJsonFeatures.forEach(f => {
			map.data.remove(f);
		});
		addParcelOutlineGeoJson(map, selectedParcels);

	};

	const addSelectedPropertyMarker = (lbltext, ctr) => {
		
		const pngMarker = {
			url: FilePath("/images/map-markers/home-icon-md.png"),
			anchor: new window.google.maps.Point(10, 10),
			labelOrigin: new window.google.maps.Point(15, -5)
		};
		var marker = new window.google.maps.Marker({
			map,
			position: ctr,
			label: { color: "#000000", fontSize: '12px', text: lbltext },
			icon: pngMarker
		});

		return marker;
	};

	const configureMapParcelInfoWindowListener = (map) => {
		window.google.maps.event.addListener(map, "click", function (evt) {
			setPopUpOpen(false);
			var geometry = fromMapOverlaysToJSON(evt.latLng, window.google.maps);
			var mapExtent = fromMapOverlaysToJSON(map.getBounds(), window.google.maps);
			var imageDisplay = "" + map.getDiv().offsetWidth + "," + map.getDiv().offsetHeight + "," + 96;
			var url = "?geometry=" + geometry + "&geometryType=esriGeometryPoint&mapExtent=" + mapExtent +
				"&tolerance=3&sr=4326&imageDisplay=" + imageDisplay + "&layers=all:1&returnGeometry=true&f=json";
			const IdentifyParcel = async () => {
				apiSentryDynamicsArcGIS.get(mapStateAbbrev +
					"/geo_" + mapStateAbbrev.toLowerCase() + "_" + mapCounty.toLowerCase() + "/MapServer/identify/"
					+ url)
					.then((response) => {
						if (response.data.results) {
							setPopUpContent(response.data.results[0].attributes);
							setPopUpPosition(evt.latLng);
							setPopUpOpen(true);
						};
					});
			}
			IdentifyParcel();
		});

	};

	const onLoad = useCallback(function callback(map) {
		if (mapStateAbbrev && mapCounty) {
			highlightParcelsOnMap(map, parcelsToShowOnMap);
			setMap(map);

			configureMapParcelInfoWindowListener(map);
		}
	}, [parcelsToShowOnMap, highlightParcelsOnMap, configureMapParcelInfoWindowListener]);


    const onUnmount = useCallback(function callback(map) {
        setMap(null)
    }, []);

     const googleMap = () => {
        var center = { lat: 43.667872, lng: -116.325073 };
        const containerStyle = {
            width: '100%',
			height: '542px'
        };
    
        return isLoaded ? (
			<GoogleMap
				id={id + "GoogleMap"}
                mapContainerStyle={containerStyle}
                defaultCenter={center}
                defaultZoom={8}
                onLoad={onLoad}
                onUnmount={onUnmount}
            >
				{popUpOpen && <InfoWindow
					position={popUpPosition}
				>
					<div className="d-flex flex-column">
						<div tabIndex="0" className="fw-bold text-center no-localization">{popUpContent.SITEADDR || popUpContent.SITUS || popUpContent.PARCELID}</div>
						<div className="fw-bold text-center no-localization">{popUpContent.OWNERNM}</div>
						<div className="fw-bold text-center no-localization">Parcel#: {popUpContent.PARCELID}</div>
						<input type="button" className="btn btn-primary" value="Create Profile" onClick={() => onSelectedMapProperty(popUpContent.PARCELID)} />
					</div>
				</InfoWindow>}
            </GoogleMap>
        ) : <></>
    }


  return (
      <>
		  {parcelsToShowOnMap && !reloadingMap && googleMap()}
      </>
  );
}

export default ResultsMap;